import { useMemo } from 'react';
import { useReadingList } from '@/hooks/use-reading-list';
import { Book } from '@/types/book';

/**
 * Enhanced reading list hook that provides additional utilities
 * and optimized checking functions for better performance
 */
export function useEnhancedReadingList() {
  const {
    readingList,
    readingListBookIds,
    isLoading,
    isBookInReadingList: originalIsBookInReadingList,
    addToReadingList,
    removeFromReadingList,
    isAdding,
    isRemoving,
  } = useReadingList();

  // Create a memoized Set for O(1) lookups instead of O(n) array searches
  const readingListBookIdsSet = useMemo(() => {
    return new Set(readingListBookIds);
  }, [readingListBookIds]);

  // Optimized function for checking if book is in reading list
  const isBookInReadingList = useMemo(() => {
    return (bookId: number) => {
      const isInList = readingListBookIdsSet.has(bookId);
      return isInList;
    };
  }, [readingListBookIdsSet]);

  // Helper function to toggle book in/out of reading list
  const toggleBookInReadingList = useMemo(() => {
    return (bookId: number) => {
      if (isBookInReadingList(bookId)) {
        removeFromReadingList(bookId);
      } else {
        addToReadingList(bookId);
      }
    };
  }, [isBookInReadingList, addToReadingList, removeFromReadingList]);

  // Filter books that are in the reading list
  const filterBooksInReadingList = useMemo(() => {
    return (books: Book[]) => {
      return books.filter(book => readingListBookIdsSet.has(Number(book.id)));
    };
  }, [readingListBookIdsSet]);

  // Get reading list with book details
  const readingListWithDetails = useMemo(() => {
    return readingList.map(item => ({
      ...item,
      // We can extend this with book details if needed
    }));
  }, [readingList]);

  return {
    // Original functions
    readingList,
    readingListBookIds,
    addToReadingList,
    removeFromReadingList,
    isLoading,
    isAdding,
    isRemoving,
    
    // Enhanced functions
    isBookInReadingList,
    toggleBookInReadingList,
    filterBooksInReadingList,
    readingListWithDetails,
    
    // Utility data
    readingListCount: readingList.length,
    isEmpty: readingList.length === 0,
    hasAny: readingList.length > 0,
  };
}